import { autoinject, transient, computedFrom } from "aurelia-framework";
import { LookupClientService } from "../../../../../../services/lookups/lookup-client.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { LookupValue, LookupValueRequest } from "../../../../../../models/lookups/lookup.models";
import { LookupValueQuery } from "../../../../../../constants/modules/lookup.constants";
import { RightShipInspectionStatusEnum, RightShipInspectionType, RightShipInspectionClientModel, RightShipInspectionCancellationEntityEnum, InspectionOutcomeFindingSummaryResponse, FindingRiskLevelEnum, FindingRiskClassificationEnum } from "../../../../../../models/rightship-inspections/rightship-inspections.models";
import { UnitOfWorkObserver } from "../../../../../../utilities/unit-of-work-observer";
import { EmailRecipientTypeEnum, SubtaskCorrespondenceEmailModel } from "../../../../../../models/vetting/vetting.models";
import { MultiSelectLookupControl } from "../../../../../components/lookups/multi-select-lookup-control/multi-select-lookup-control";
import { RightshipInspectionStatusMarker } from "./../rightship-inspection-status-marker";
import * as DOMPurify from "dompurify";
import { RightShipInspectionsService } from "../../../../../../services/rightship-inspections/rightship-inspections.service";
import { ValidationController, ValidationRules } from "aurelia-validation";
import { CustomValidationRenderer } from "../../../../../../client-validation-system/custom-validation-renderer";
import { ValidationsClientService } from "../../../../../../services/validations/validations-client.service";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import * as moment from "moment";
import { LookupControl } from '../../../../../components/lookups/lookup-control/lookup-control';
import { Claims } from '../../../../../../constants/authentication/claims';
import { UserClaimsService } from '../../../../../../services/authentication/user-claims.service';
import { EmailValidator } from '../../../../../../utilities/email-validator';
import Quill from "quill";
import { RightShipDialogServiceConstants } from '../../../../../../constants/basics/rightship-dialog.constants';
import { InsightIndicatorModel } from '@rightship-tech/rs-inspections-ui/models/rightship-inspection.models';
import { NotificationClientService, NotificationType } from '@rightship-tech/common-ui/services/basic/notification-client.service';

window["Quill"] = Quill;
if (!Quill["imports"]['formats/rightship-inspection-status-marker']) {
    Quill.register({
        'formats/rightship-inspection-status-marker': RightshipInspectionStatusMarker
    });
}

@autoinject
@transient()
export class RightshipInspectionStatusDialog {

    mslcInProgress: MultiSelectLookupControl;
    lcCancel: LookupControl;
    mslcUnacceptable: MultiSelectLookupControl;
    linkedVetIds: number[] = [];
    validityExpiryDate: Date;
    hasTech: boolean = true;
    hasCompany: boolean = true;
    reload: any;
    dateOfInspection: Date;
    unacceptable: number = RightShipInspectionStatusEnum.Unacceptable;
    acceptable: number = RightShipInspectionStatusEnum.Acceptable;
    awaitingResponse: number = RightShipInspectionStatusEnum.AwaitingResponse;
    inProgress: number = RightShipInspectionStatusEnum.InProgress;
    firstReviewDone: number = RightShipInspectionStatusEnum.FirstReviewDone;
    canceled: number = RightShipInspectionStatusEnum.Cancelled;
    scheduled: number = RightShipInspectionStatusEnum.Scheduled;
    rightShipInspectionId: number;
    summary: string;
    
    comments: string;
    statusSnippet: string;
    reasonIds: number[] = [];
    reasonForCancellationId: number;
    meta: any = {};
    isDropdownOpen: boolean;
    statusSubmitted: boolean;
    deficienciesReviewed: boolean = false;
    fullInspectionReportReviewed: boolean = false;
    notifyShipManager: boolean = true;
    rightShipInspectionStatusId: number;
    rsInspectionGradeId: number;
    inspectingCompanyId: number;
    item: RightShipInspectionClientModel;
    rightShipInspectionStatuses: LookupValue[];
    currentRightShipInspectionStatus: LookupValue;
    port: string;
    vesselName: string;
    inspectionType: string;
    inspectionTypeId: number;
    internationalMaritimeOrganizationNumber: string;
    europeanNumberOfIdentification: string;
    vehicleIdentificationNumber: string;
    emails: SubtaskCorrespondenceEmailModel[] = [];
    rootCauseAnalysisResponseRatingId: number;
    correctiveActionResponseRatingId: number;
    preventiveActionResponseRatingId: number;
    inspectingCompanyEmail: string;
    isFirstReviewedDone: boolean;
    correspondenceRecipientType: string = '(Correspondence)';
    whoCancelledId: number;
    selectedReasonForCancellation: any;
    selectedWhoCancelled: any;
    reasonState: string;
    statusName: string;
    canCloseWitoutFindings: boolean = false;
    reasonForDurationOfValidityId: number;
    reasonForDurationOfValidity: LookupValue;
    additionalCommentsForDurationOfValidity: string;
    acceptableCloseoutTypeId: number;
    unacceptableCloseoutTypeId: number;
    closeoutInspectionErrors: string;
    isOnlyUnacceptable: boolean;
    isShowStopper: boolean;
    isOverwriteValidity: boolean;
    closeoutTypeId: number;
    totalFindings: number;
    formHasBeenSubmitted: boolean;
    closeoutType?: string;
    public loadingValidityMonth: boolean = false;
    public riskLevelsData: InspectionOutcomeFindingSummaryResponse;
    public insightIndicators: InsightIndicatorModel[];
    public selectedCloseoutItem?: LookupValue;
    today = () => {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        return date;
    }

    //Determines whether the 'Inspection is valid for' date is in months, or a date selection
    isInspectionValidityTypeInMonths: boolean = true;

    months: { id: number, name: string }[] = [12, 9, 6, 3, 0].map(x => {
        return { id: x, name: x == 1 ? "1 month" : `${x} months` }
    });

    _validUntilMonthsOffset: number;
    _validUntilDate: Date;
    _inspectingCompany: LookupValue;

    @computedFrom('inspectionTypeId')
    get isWet() {
        return this.inspectionTypeId == RightShipInspectionType.TankerInspection;
    }

    @computedFrom('inspectionTypeId')
    get isCargill() {
        return this.inspectionTypeId == RightShipInspectionType.CargillSurvey;
    }

    @computedFrom('inspectionTypeId')
    get isDry() {
        return this.inspectionTypeId == RightShipInspectionType.RightShipInspection;
    }

    @computedFrom('_validUntilMonthsOffset')
    get validUntilMonthsOffset() {
        return this._validUntilMonthsOffset;
    }

    set validUntilMonthsOffset(month: number) {
        this._validUntilMonthsOffset = month;
        this.setCorrespondenceMessage();
    }

    @computedFrom('_validUntilDate')
    get validUntilDate() {
        return this._validUntilDate;
    }

    set validUntilDate(date : Date) {
        this._validUntilDate = date;
        this.setCorrespondenceMessage();
    }

    @computedFrom('_inspectingCompany')
    get inspectingCompany(): LookupValue {
        return this._inspectingCompany;
    }

    set inspectingCompany(x: LookupValue) {
        const oldValue = this._inspectingCompany;
        this._inspectingCompany = x;
        if (oldValue || !this.inspectingCompanyEmail) {
            this.setInspectingCompanyEmail();
        }
        this.setCorrespondenceMessage();
    }

    @computedFrom('inspectionTypeId')
    public get rightshipEmail(): string {
        switch (this.inspectionTypeId) {
            case RightShipInspectionType.RightShipInspection: return "dryinspections@rightship.com";
            case RightShipInspectionType.CargillSurvey: return "tankerinspections@rightship.com";
            case RightShipInspectionType.TankerInspection: return "tankerinspections@rightship.com";
        }
        return "";
    }

    @computedFrom('validityExpiryDate', 'isCloseOut', 'dateOfInspection', '_validUntilMonthsOffset' , '_validUntilDate')
    get validUntil(): Date {
        if (this.validityExpiryDate && !this.isCloseOut) return this.validityExpiryDate;

        let requestedDateOfInspection = (this.dateOfInspection) ? CloneHelper.clone(this.dateOfInspection) : new Date();
        
        if(typeof requestedDateOfInspection == "string")  requestedDateOfInspection = new Date(requestedDateOfInspection);

        if(this.isInspectionValidityTypeInMonths) {
            return new Date(requestedDateOfInspection.setMonth(requestedDateOfInspection.getMonth() + this.validUntilMonthsOffset))
        }
        return this._validUntilDate;
    }

    @computedFrom('rightShipInspectionStatusId', 'currentRightShipInspectionStatus')
    get submitEnabled(): boolean {
        return this.currentRightShipInspectionStatus && this.rightShipInspectionStatusId != this.currentRightShipInspectionStatus.id;
    }

    @computedFrom('currentRightShipInspectionStatus')
    get isCloseOut() {
        const statuses = [
            RightShipInspectionStatusEnum.Acceptable,
            RightShipInspectionStatusEnum.Unacceptable,
            RightShipInspectionStatusEnum.Cancelled
        ];

        return this.currentRightShipInspectionStatus && statuses.indexOf(this.currentRightShipInspectionStatus.id) != -1;
    }

    @computedFrom('currentRightShipInspectionStatus')
    get isAcceptable() {
        const statuses = [
            RightShipInspectionStatusEnum.Acceptable
        ];

        return this.currentRightShipInspectionStatus && statuses.indexOf(this.currentRightShipInspectionStatus.id) != -1;
    }

    @computedFrom('currentRightShipInspectionStatus')
    get isUnacceptable() {
        const statuses = [
            RightShipInspectionStatusEnum.Unacceptable
        ];

        return this.currentRightShipInspectionStatus && statuses.indexOf(this.currentRightShipInspectionStatus.id) != -1;
    }

    @computedFrom('currentRightShipInspectionStatus')
    get isCancelled() {
        const statuses = [
            RightShipInspectionStatusEnum.Cancelled
        ];

        return this.currentRightShipInspectionStatus && statuses.indexOf(this.currentRightShipInspectionStatus.id) != -1;
    }

    @computedFrom('rightShipInspectionStatusId')
    get wasClosed() {
        const statuses = [
            RightShipInspectionStatusEnum.Acceptable,
            RightShipInspectionStatusEnum.Unacceptable,
            RightShipInspectionStatusEnum.Cancelled
        ];

        return statuses.indexOf(this.rightShipInspectionStatusId) != -1;
    }
    
    @computedFrom('selectedReasonForCancellation', 'selectedWhoCancelled')
    get isOtherCancellationReason(){
        return this.selectedWhoCancelled 
                && this.selectedReasonForCancellation 
                && (this.selectedWhoCancelled.name.toLowerCase() == RightShipInspectionCancellationEntityEnum.Invalid.toLowerCase() 
                    || this.selectedWhoCancelled.name.toLowerCase() == RightShipInspectionCancellationEntityEnum.Incomplete.toLowerCase())
                && this.selectedReasonForCancellation.name.toLowerCase() == 'other';
    }

    @computedFrom('selectedReasonForCancellation', 'selectedWhoCancelled')
    get isNewInformationCancellationReason(){
        return this.selectedWhoCancelled 
                && this.selectedReasonForCancellation 
                && this.selectedWhoCancelled.name.toLowerCase() == RightShipInspectionCancellationEntityEnum.Invalid.toLowerCase() 
                && this.selectedReasonForCancellation.name.toLowerCase() == 'new information received';
    }

    @computedFrom('isOtherCancellationReason', 'isNewInformationCancellationReason')
    get showStateReason(){
        return this.isOtherCancellationReason || this.isNewInformationCancellationReason;
    }

    constructor(private lookupClientService: LookupClientService,
        private dialogClientService: DialogClientService,
        private validationsClientService: ValidationsClientService,
        private inspectionClientService: RightShipInspectionsService,
        private validationController: ValidationController,
        private userClaimService: UserClaimsService,
        private notificationService: NotificationClientService) {
        this.validationController.addRenderer(new CustomValidationRenderer());
        this.addValidationRules();

        this.canCloseWitoutFindings = this.userClaimService.hasPermission(Claims.CloseInspectionWithoutGradingFindings);
    }

    addValidationRules() {
        ValidationRules
            .ensure<RightshipInspectionStatusDialog, number>(x => x.rootCauseAnalysisResponseRatingId)
            .displayName('Root cause analysis')
            .required()
            .when(x => x.isCloseOut && x.isDry && x.currentRightShipInspectionStatus.id != x.canceled)

            .ensure<number>(x => x.correctiveActionResponseRatingId)
            .displayName('Corrective action')
            .required()
            .when(x => x.isCloseOut && x.isDry && x.currentRightShipInspectionStatus.id != x.canceled)

            .ensure<number>(x => x.preventiveActionResponseRatingId)
            .displayName('Preventative action')
            .required()
            .when(x => x.isCloseOut && x.isDry && x.currentRightShipInspectionStatus.id != x.canceled)

            //closeout email validations
            .ensure<SubtaskCorrespondenceEmailModel[]>(x => x.emails)
            .required()
            .withMessage("At least one email is required")
            .when(x => x.isCloseOut && x.notifyShipManager)
            .satisfies(emails => emails.filter(e => e.isTechnicalManagerEmail && e.email).length > 0)
            .withMessage("At least one email is required")
            .when(x => x.isCloseOut && x.notifyShipManager)
            
            .ensure<SubtaskCorrespondenceEmailModel[]>(x => x.emails)
            .satisfies(emails => emails.filter(e => (!e.isInspectingCompanyEmail || (e.isInspectingCompanyEmail && e.email != null)) && !EmailValidator.isValidEmail(e.email)).length == 0)
            .withMessage("Email is not in correct format")

            .ensure<number>(x=> x.whoCancelledId)
            .required()
            .withMessage("Who cancelled the inspection is required")
            .when(x => x.currentRightShipInspectionStatus.id == x.canceled)

            .ensure<number[]>(x => x.reasonIds)
            .satisfies(reasonIds => reasonIds && reasonIds.length > 0)
            .withMessage("Reason for status change is required")
            .when(() => this.currentRightShipInspectionStatus.id == RightShipInspectionStatusEnum.InProgress && this.wasClosed)
            
            .ensure<number>(x => x.reasonForCancellationId)
            .required()
            .withMessage("Reason for status change is required")
            .when(x => x.currentRightShipInspectionStatus.id == x.canceled)

            .ensure<string>(x => x.reasonState)
            .required()
            .withMessage("State reason for status change is required")
            .when(x => x.showStateReason)

            .ensure<string>(x => x.closeoutInspectionErrors)
            .satisfies(() => this.deficienciesReviewed != false || this.fullInspectionReportReviewed != false)
            .withMessage('Selecting the reviewed items before closing out the inspection is required')

            .ensure<number>(x => x.rsInspectionGradeId)
            .required()
            .withMessage("Overall assessment is required")

            .ensure<Date>(x => x.validUntilDate)
            .required()
            .withMessage("Select or enter date is required")
            .when(x => !x.isInspectionValidityTypeInMonths)

            .ensure<number>(x => x.validUntilMonthsOffset)
            .satisfies(x => x >= 0)
            .withMessage("Select number of months is required")
            .when(x => x.isInspectionValidityTypeInMonths)

            .on(this);


    }

    @computedFrom("emails", "emails.length")
    get technicalManagerEmails() {
        return this.emails.filter(x => x.isTechnicalManagerEmail);
    }

    @computedFrom("emails", "emails.length")
    get inspectingCompanyEmails() {
        return this.emails.filter(x => x.isInspectingCompanyEmail);
    }

    @computedFrom("isInspectionValidityTypeInMonths", "validUntilMonthsOffset")
    get showReasonForDurationOfValidityId() {
        return this.isInspectionValidityTypeInMonths && [3,6,9, 12].includes(this.validUntilMonthsOffset,0) || this.isUnacceptable;
    }

    @computedFrom("acceptableCloseoutTypeId", "validUntilMonthsOffset")
    get acceptableReasonForDurationParameters() {
        return `false,${this.acceptableCloseoutTypeId ? this.acceptableCloseoutTypeId : 'NULL' },${this.validUntilMonthsOffset ? this.validUntilMonthsOffset : 'NULL' }`;
    }

    @computedFrom("unacceptableCloseoutTypeId")
    get unacceptableReasonForDurationParameters() {
        return `false,${this.unacceptableCloseoutTypeId ? this.unacceptableCloseoutTypeId : 'NULL' },NULL`;
    }

    async activate(model: {
        item: RightShipInspectionClientModel,
        port: string,
        date: Date,
        reload: () => void,
        subtaskTypes: number[],
        emails: SubtaskCorrespondenceEmailModel[],
        inspectionType: string
    }) {
        if (!model) return;

        this.reload = model.reload;
        this.inspectionType = model.inspectionType;
        this.inspectionTypeId = Number(model.item.inspectionTypeId);
        this.rightShipInspectionId = model.item.rightShipInspectionId;
        this.inspectingCompanyId = model.item.inspectingCompanyId;
        this.item = model.item;
        this.inspectingCompanyEmail = model.item.inspectingCompanyEmail;
        this.rightShipInspectionStatusId = model.item.rightShipInspectionStatusId;
        this.vesselName = model.item.vesselName;
        this.dateOfInspection = model.date;
        this.port = model.port;
        this.internationalMaritimeOrganizationNumber = model.item.vesselImo;
        this.europeanNumberOfIdentification = model.item.europeanNumberOfIdentification;
        this.vehicleIdentificationNumber = model.item.vehicleIdentificationNumber;
        this.reasonIds = model.subtaskTypes;

        this.validityExpiryDate = model.item.validityExpiryDate;
        this.rsInspectionGradeId = model.item.rightShipInspectionGradeId;
        this.fullInspectionReportReviewed = model.item.fullInspectionReportReviewed;
        this.deficienciesReviewed = model.item.allDeficienciesReviewed;
        this.notifyShipManager = this.inspectionTypeId == RightShipInspectionType.CargillSurvey ? null : model.item.notifyShipManagerOnCloseOut;
        this.rootCauseAnalysisResponseRatingId = model.item.rootCauseAnalysisResponseRatingId;
        this.correctiveActionResponseRatingId = model.item.correctiveActionResponseRatingId;
        this.preventiveActionResponseRatingId = model.item.preventiveActionResponseRatingId;
        this.reasonForDurationOfValidityId = model.item.reasonForDurationOfValidityId;
        this.additionalCommentsForDurationOfValidity = model.item.additionalCommentsForDurationOfValidity;
        this.formHasBeenSubmitted = model.item.formHasBeenSubmitted;
        if (model.emails) {
            this.emails = model.emails;
        }

        if (this.emails.filter(x => x.isInspectingCompanyEmail).length == 0) {
            const x = this.addEmail(false, false, true);
            x.emailRecipientTypeId = EmailRecipientTypeEnum.To;
            this.setInspectingCompanyEmail();
        }

        if (this.emails.filter(x => x.isTechnicalManagerEmail).length == 0) {
            const x = this.addEmail(true, false, false);
            x.emailRecipientTypeId = EmailRecipientTypeEnum.To;
        }

        const request = new LookupValueRequest();
        request.parameters = `${this.isCargill ? '1' : '0'},${this.isWet ? '1' : '0'},${this.rightShipInspectionId}`;
        request.lookupValueQuery = LookupValueQuery.RightShipInspectionStatuses;
        const response = await this.lookupClientService.getLookup(request);
        this.rightShipInspectionStatuses = response.activeValues;
        if (this.wasClosed) {
            this.rightShipInspectionStatuses = this.rightShipInspectionStatuses.filter(x => x.id == RightShipInspectionStatusEnum.InProgress || x.id == this.rightShipInspectionStatusId)
        }

        this.isOnlyUnacceptable = model.item.isShowStopper && this.rightShipInspectionStatuses.some(x => x.id == RightShipInspectionStatusEnum.Acceptable);
        if (this.isOnlyUnacceptable) {
            this.rightShipInspectionStatuses = this.rightShipInspectionStatuses.filter(x => x.id == RightShipInspectionStatusEnum.Unacceptable || x.id == this.rightShipInspectionStatusId)
        }
        this.currentRightShipInspectionStatus = this.rightShipInspectionStatuses.filter(x => x.id == (this.wasClosed ? RightShipInspectionStatusEnum.InProgress : this.rightShipInspectionStatusId))[0];
    }
    
    openDropdown(event) {
        event.stopPropagation();
        this.isDropdownOpen = !this.isDropdownOpen;
    }

    closeDropdown() {
        this.isDropdownOpen = false;
    }

    changeRightShipInspectionStatus(rightShipInspectionStatus: LookupValue) {
        if (!this.currentRightShipInspectionStatus || this.currentRightShipInspectionStatus.id != rightShipInspectionStatus.id) {
            this.meta = {};
        }

        this.currentRightShipInspectionStatus = rightShipInspectionStatus;
        this.lastSelectedItemIds = [];

        this.statusSnippet = null;
        this.reasonIds = [];
        this.reasonForCancellationId = null;

        this.statusName = this.currentRightShipInspectionStatus.name;

        if(RightShipInspectionStatusEnum.Acceptable || RightShipInspectionStatusEnum.Unacceptable){
            if (this.item.correspondenceEmail){
                const correspondenceEmail: SubtaskCorrespondenceEmailModel ={
                    email: this.item.correspondenceEmail,
                    emailRecipientTypeId: EmailRecipientTypeEnum.Cc,
                    recipientType: this.correspondenceRecipientType
                };
                this.emails.push(correspondenceEmail);
            }
        }
        
        this.setCorrespondenceMessage();

        //Get only for closeout and the first time status change
        if (!this.isCargill && this.isCloseOut && !this.riskLevelsData) {
            this.inspectionClientService.getInspectionOutcomeFindingSummary(this.rightShipInspectionId).then(x => {
                this.riskLevelsData = x;
                this.totalFindings = this.riskLevelsData.total;
            });
        }
        if (!this.isCargill && this.isCloseOut && !this.insightIndicators) {
            this.inspectionClientService.getAiInsights(this.rightShipInspectionId).then(x => {
                this.insightIndicators = x.insightIndicators;
                const hasAiData = this.insightIndicators 
                                && this.insightIndicators.some(x => x.insights && x.insights.length > 0 
                                                            && x.insights.some(y => y.description));
                if (!hasAiData) {
                    this.notificationService.show("AI Insights", "Unable to get AI insight.", NotificationType.Warning);
                }
            }).catch(() => {
                this.notificationService.show("AI Insights", "Unable to get AI insight.", NotificationType.Warning);
            });
        }
    }

    reviewInspectionReport() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.ReviewInspectionReportDialog, {
            onProceed: () => {
                return this.submit();
            },
            validUntil: this.validUntil, //valid date
            validUntilText: this.validUntilText, //valid months
            statusName: this.statusName,
            statusId: this.currentRightShipInspectionStatus.id,
            inspectionId: this.rightShipInspectionId,
            summary: this.summary,
            formHasBeenSubmitted: this.formHasBeenSubmitted,
            insightIndicators: this.insightIndicators,
            closeoutType: this.selectedCloseoutItem?.name,
        }, true, false, false)
    }

    getBarCssClass(name: string): string {
        return name
        .toLowerCase()
        .replace('Risk', '')
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .trim();
    }

    cancel() {
        this.dialogClientService.hide();
    }

    async submit() {
        try {
            const validationResponse = await this.validationController.validate();
           
            if (!validationResponse.valid) return false;

            if(!this.canCloseWitoutFindings && this.item.hasIncompleteFinding 
                && (this.currentRightShipInspectionStatus.id == this.acceptable || this.currentRightShipInspectionStatus.id == this.unacceptable || this.currentRightShipInspectionStatus.id == this.firstReviewDone))
            {
                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.GradingIncompleteDialog, {});
                return false;
            }

            const observer = UnitOfWorkObserver.getTrackingInstance(null, {}, []);

            if (this.isCloseOut) {
                if (this.notifyShipManager) {

                    if (this.emails.filter(x => x.emailRecipientTypeId == EmailRecipientTypeEnum.To).length <= 0) {
                        //if none of the emails are of type to then set 1st one as To email
                        this.emails[0].emailRecipientTypeId == EmailRecipientTypeEnum.To;
                    }
                }

                if (this.currentRightShipInspectionStatus.id == RightShipInspectionStatusEnum.Acceptable) {
                    const validityDate = this.isCargill ? this.validityExpiryDate : this.validUntil;
                    if (typeof validityDate == "string") {
                        observer.addChange("validityExpiryDate", validityDate, null);
                    }
                    else {
                        observer.addChange("validityExpiryDate", validityDate && !isNaN(validityDate.getTime()) ? validityDate.toISOString() : null, null);
                    }
                    observer.addChange("closeoutTypeId", this.closeoutTypeId, this.item.closeoutTypeId);

                }
                else {
                    this.rsInspectionGradeId = null;
                }

                observer.addChange("rightShipInspectionGradeId", this.rsInspectionGradeId, this.item.rightShipInspectionGradeId);
                observer.addChange("fullInspectionReportReviewed", this.fullInspectionReportReviewed, this.item.fullInspectionReportReviewed);
                observer.addChange("allDeficienciesReviewed", this.deficienciesReviewed, this.item.allDeficienciesReviewed);
                observer.addChange("notifyShipManagerOnCloseOut", this.notifyShipManager, null); //https://rightship.atlassian.net/browse/PX-10224
                observer.addChange("rootCauseAnalysisResponseRatingId", this.rootCauseAnalysisResponseRatingId, this.item.rootCauseAnalysisResponseRatingId);
                observer.addChange("correctiveActionResponseRatingId", this.correctiveActionResponseRatingId, this.item.correctiveActionResponseRatingId);
                observer.addChange("preventiveActionResponseRatingId", this.preventiveActionResponseRatingId, this.item.preventiveActionResponseRatingId);
                if((this.isAcceptable || this.isUnacceptable) && !this.showReasonForDurationOfValidityId){
                    this.reasonForDurationOfValidityId = null;
                }
                observer.addChange("reasonForDurationOfValidityId", this.reasonForDurationOfValidityId, this.item.reasonForDurationOfValidityId);
                observer.addChange("additionalCommentsForDurationOfValidity", this.additionalCommentsForDurationOfValidity, this.item.additionalCommentsForDurationOfValidity);
                observer.addChange("summary", this.summary, this.item.summary);

                if (!this.isCargill && this.insightIndicators) {
                    this.insightIndicators.forEach(insightIndicator => {
                        insightIndicator.insights.forEach(insight => {
                            if (insightIndicator.id == FindingRiskLevelEnum.High && insight.id == FindingRiskClassificationEnum.Regulatory && insight.description != this.item.regulatoryHighRiskInsightSummary) {
                                observer.addChange("regulatoryHighRiskInsightSummary", insight.description, this.item.regulatoryHighRiskInsightSummary);
                            }
                            if (insightIndicator.id == FindingRiskLevelEnum.High && insight.id == FindingRiskClassificationEnum.IndustryStandard && insight.description != this.item.standardHighRiskInsightSummary) {
                                observer.addChange("standardHighRiskInsightSummary", insight.description, this.item.standardHighRiskInsightSummary);
                            }
                            if (insightIndicator.id == FindingRiskLevelEnum.Medium && insight.id == FindingRiskClassificationEnum.Regulatory && insight.description != this.item.regulatoryMediumRiskInsightSummary) {
                                observer.addChange("regulatoryMediumRiskInsightSummary", insight.description, this.item.regulatoryMediumRiskInsightSummary);
                            }
                            if (insightIndicator.id == FindingRiskLevelEnum.Medium && insight.id == FindingRiskClassificationEnum.IndustryStandard && insight.description != this.item.standardMediumRiskInsightSummary) {
                                observer.addChange("standardMediumRiskInsightSummary", insight.description, this.item.standardMediumRiskInsightSummary);
                            }
                            if (insightIndicator.id == FindingRiskLevelEnum.Low && insight.id == FindingRiskClassificationEnum.Regulatory && insight.description != this.item.regulatoryLowRiskInsightSummary) {
                                observer.addChange("regulatoryLowRiskInsightSummary", insight.description, this.item.regulatoryLowRiskInsightSummary);
                            }
                            if (insightIndicator.id == FindingRiskLevelEnum.Low && insight.id == FindingRiskClassificationEnum.IndustryStandard && insight.description != this.item.standardLowRiskInsightSummary) {
                                observer.addChange("standardLowRiskInsightSummary", insight.description, this.item.standardLowRiskInsightSummary);
                            }
                        });
                    });
                }
            }

            const meta = JSON.stringify(this.meta);

            if (this.currentRightShipInspectionStatus.id == this.inProgress && this.mslcInProgress) {
                this.statusSnippet = this.mslcInProgress.itemSource.filter(x => this.reasonIds.indexOf(x.id) != -1).map(x => x.name).join(", ");
            }

            if (this.currentRightShipInspectionStatus.id == this.canceled && this.lcCancel) {
                this.statusSnippet = this.lcCancel.itemSource.filter(x => this.reasonIds.indexOf(x.id) != -1).map(x => x.name).join(", ");
            }

            if((this.isOtherCancellationReason || this.isNewInformationCancellationReason) && this.isCancelled) {
                this.statusSnippet = this.reasonState;
            }
            
            const response = await this.inspectionClientService.changeStatus(this.rightShipInspectionId, this.currentRightShipInspectionStatus.id, this.statusName,
                this.statusSnippet, this.comments, meta, this.reasonIds, this.emails.filter(x => x.email), observer.getChanges());

            if (response.validationResult) {
                this.validationsClientService.showValidationErrors(response.validationResult);
                return false;
            }

            this.statusSubmitted = true;
            if (this.reload) {
                this.reload();
            }
            if (this.linkedVetIds.length == 0)
                this.dialogClientService.hide();
            return true;
        } catch (e) {
            console.error(e);
            return false;
        }
    }

    setInspectingCompanyEmail() {
        const inspectingCompanyEmails = this.emails.filter(x => x.isInspectingCompanyEmail);
        if (inspectingCompanyEmails?.length > 0) {
            inspectingCompanyEmails[0].email = this.inspectingCompany && this.inspectingCompany.additionalData ? this.inspectingCompany.additionalData["toEmail"] : this.inspectingCompanyEmail;
        }
        else {
            this.addEmail(false, false, true);
            this.setInspectingCompanyEmail();
        }
    }

    @computedFrom("validUntilMonthsOffset")
    get validUntilText() {
        if (this.validUntilMonthsOffset == undefined || this.validUntilMonthsOffset == null) return '';
        return `${this.validUntilMonthsOffset} months`;
    }

    @computedFrom("validUntilMonthsOffset")
    get validityClass() {
        if (!this.validUntilMonthsOffset) return "";
        return this.validUntilMonthsOffset && this.validUntilMonthsOffset < 12 ? "" : "equal-12";
    }
    
    setCorrespondenceMessage() {
        if (this.currentRightShipInspectionStatus) {
            if (this.currentRightShipInspectionStatus.id == RightShipInspectionStatusEnum.Unacceptable && this.isDry) {
                this.comments = `<p style="font-size: 15px;">Good Day,</p>
                                <p style="font-size: 15px;">Your corrective and preventative action plan for the subject vessel has been reviewed and accepted.</p>
                                <p style="font-size: 15px;">Please be advised that the RightShip inspection is now completed and has been marked as unacceptable.</p>
                                <p style="font-size: 15px;">In order to meet the enhanced safety requirements of our Chartering, Terminal and Port customers, RightShip implemented RISQ (RightShip Inspection Ship Questionnaire) for all vetting Inspections from September 2022. Not only does RISQ seek to ensure compliance with the requirements of International conventions and regulations it also looks at adoption of Industry best practices related to the safe operation and management of vessels. To be deemed acceptable and receive forward validity the vessel must demonstrate compliance with relevant parts of the entire document.</p>
                                <p style="font-size: 15px;">The subject vessel has been found unacceptable at inspection as it fails, in our assessment, to meet requirements as evidenced by the number and nature of findings recorded by the inspector.</p>
                                <p style="font-size: 15px;">We recommend that Owners/Managers carefully review all recorded findings (links to photographs are provided where available in the inspection report), and the associated RISQ guide to inspection.</p>
                                <p style="font-size: 15px;"><b>Important</b> - the RISQ inspection is based on random sample methodology and where shortcomings are identified on an item (i.e a crane or a hold ladder) it is incumbent on Owners/Managers to undertake a careful inspection and take remedial action, where appropriate, of all of these items on the vessel.</p>
                                <p style="font-size: 15px;">Where there is any uncertainty about the veracity of a recorded finding or the conclusion of an inspection, we ask that you forward your enquiry to email <a>risq@rightship.com</a></p>
                                <p style="font-size: 15px;">The information relating to a vessel's RightShip rating is available to RightShip customers only. Although the vessel has been inspected by RightShip, the owners/managers being inspected are not to directly or indirectly state, indicate or convey the impression that the vessel has been approved, endorsed or recommended by RightShip.</p>
                                <p style="font-size: 15px;">Gentle Reminder - RightShip would like to take this opportunity to remind you that as per our terms and conditions settlement of the inspection invoice is required within 14 days of issue. Please ignore if already actioned.</p>
                                <p style="font-size: 15px;">In order to develop and refine our inspection service we invite you to click on the link below and complete our short inspection feedback survey:</p>
                                <p><span>{{close out Survey}}</span></p>
                                <p style="font-size: 15px;">Your response is highly valued and will be used to develop and improve our inspections process.</p>
                                <p style="font-size: 15px;">Thank you in advance for your time and we look forward to your feedback.</p>
                                <p style="font-size: 15px;">Best Regards</p>
                                `;
            } else if (this.currentRightShipInspectionStatus.id == RightShipInspectionStatusEnum.Cancelled && this.isDry) {
                let status = "cancelled";
                if(this.selectedWhoCancelled && this.selectedWhoCancelled.name.toLowerCase() == RightShipInspectionCancellationEntityEnum.Invalid.toLowerCase()) {
                    status = "invalidated";
                    this.statusName = "Invalid";
                }
                else if(this.selectedWhoCancelled && this.selectedWhoCancelled.name.toLowerCase() == RightShipInspectionCancellationEntityEnum.Incomplete.toLowerCase()) {
                    status = "marked as incomplete";
                    this.statusName = "Incomplete";
                }
                this.comments = `Good Day,
                <br />Please be informed that RightShip inspection request (#${this.rightShipInspectionId}) has been ${status} due to the below followings:`;
                if(this.selectedWhoCancelled){
                    const div = document.createElement('div');
                    div.innerHTML = DOMPurify.sanitize(this.comments);
                    let whoParagraph = div.querySelector('.who-cancelled');
                    if (whoParagraph == null) {
                        const whoDiv = document.createElement('p');
                        whoDiv.classList.add('who-container');
                        div.append(whoDiv);

                        const whoTitle = document.createElement('p')
                        whoTitle.textContent = "Who";
                        whoDiv.append(whoTitle);

                        whoParagraph = document.createElement('p');
                        whoParagraph.classList.add('who-cancelled');
                        whoDiv.append(whoParagraph);
                    }

                    whoParagraph.textContent = this.selectedWhoCancelled.name;
                    this.comments = div.innerHTML;
                    div.remove();
                }
            } else if (this.currentRightShipInspectionStatus.id == RightShipInspectionStatusEnum.Acceptable && this.isDry) {
                this.comments = `<p style="font-size: 15px;">Good Day,</p>
                                <p style="font-size: 15px;">Your corrective and preventative action plan for the subject vessel has been reviewed and accepted.</p>
                                <p style="font-size: 15px;">Please be advised that the RightShip inspection is now completed.</p>
                                <p style="font-size: 15px;">In order to meet the enhanced safety requirements of our Chartering, Terminal and Port customers, RightShip implemented RISQ (RightShip Inspection Ship Questionnaire) for all vetting Inspections from September 2022. Not only does RISQ seek to ensure compliance with the requirements of International conventions and regulations it also looks at adoption of Industry best practices related to the safe operation and management of vessels. To be deemed acceptable and receive forward validity the vessel must demonstrate compliance with relevant parts of the entire document.</p>
                                <p style="font-size: 15px;">The inspection is valid for ${this.validUntilText} from the date of inspection, subject to satisfactory PSC performance, no serious incidents and meeting customer requirements.</p>
                                <p style="font-size: 15px;">In determining the inspection validity the findings have been graded with the following outcome.</p>
                                <p style="font-size: 15px;">We recommend that Owners/Managers carefully review all recorded findings (links to photographs are provided where available in the inspection report), and the associated RISQ guide to inspection.</p>
                                <p style="font-size: 15px;"><b>Important</b> - the RISQ inspection is based on random sample methodology and where shortcomings are identified on an item (i.e a crane or a hold ladder) it is incumbent on Owners/Managers to undertake a careful inspection and take remedial action, where appropriate, of all of these items on the vessel.</p>
                                <p style="font-size: 15px;">Where there is any uncertainty about the veracity of a recorded finding or the conclusion of an inspection, we ask that you forward your enquiry to email <a>risq@rightship.com</a></p>
                                <p style="font-size: 15px;">A RightShip Inspection badge will be promoted on the vessel page.</p>
                                <p style="font-size: 15px;">The information relating to a vessel's RightShip rating is available to RightShip customers only. Although the vessel has been inspected by RightShip, the owners/managers being inspected are not to directly or indirectly state, indicate or convey the impression that the vessel has been approved, endorsed or recommended by RightShip.</p>
                                <p style="font-size: 15px;">Gentle Reminder - RightShip would like to take this opportunity to remind you that as per our terms and conditions settlement of the inspection invoice is required within 14 days of issue. Please ignore if already actioned.</p>
                                <p style="font-size: 15px;">In order to develop and refine our inspection service we invite you to click on the link below and complete our short inspection feedback survey:</p>
                                <p><span>{{close out Survey}}</span></p>
                                <p style="font-size: 15px;">Your response is highly valued and will be used to develop and improve our inspections process.</p>
                                <p style="font-size: 15px;">Thank you in advance for your time and we look forward to your feedback.</p>
                                <p style="font-size: 15px;">Best Regards</p>
                `;
            } else {
                this.comments = "";
            }
        } else {
            this.comments = "";
        }
    }

    lastSelectedItemIds: number[] = [];

    selectedItemsChangedForAwaitingResponse($event) {
        const notSelected = $event.detail.filter(x => !x.isSelected);

        if (this.statusSnippet) {
            notSelected.forEach(x => {
                const index = this.lastSelectedItemIds.indexOf(x.id);
                if (index >= 0)
                    this.lastSelectedItemIds.splice(index, 1);
                this.statusSnippet = this.statusSnippet.split(`, ${x.name}`).join("");
                this.statusSnippet = this.statusSnippet.split(`${x.name}, `).join("");
                this.statusSnippet = this.statusSnippet.split(x.name).join("");
            });
        }

        const newItems = $event.detail.filter(x => x.isSelected && this.lastSelectedItemIds.indexOf(x.id) == -1);
        newItems.forEach(x => {
            if (this.statusSnippet && !this.statusSnippet.endsWith(", "))
                this.statusSnippet += ", ";
            else if (!this.statusSnippet) {
                this.statusSnippet = "";
            }

            if (x && x.name)
                this.statusSnippet += x.name;

            this.lastSelectedItemIds.push(x.id);
        });
    }

    selectedItemsChanged($event) {
        if (this.currentRightShipInspectionStatus && this.currentRightShipInspectionStatus.id == RightShipInspectionStatusEnum.InProgress) return;
        if (this.isCancelled && this.isWet) return;

        const isCancelling: boolean = this.currentRightShipInspectionStatus.id == this.canceled && this.lcCancel != null;
        // Must be inside the Cancellation Status
        if (this.reasonForCancellationId != null){
            this.reasonIds = [];
            this.reasonIds[0] = this.reasonForCancellationId;
        }

        let notSelected: any;
        if (!isCancelling){
            notSelected = $event.detail.filter(x => !x.isSelected);
        }
        
        let ending = "";
        let start = "";

        if (this.comments) {
            const unacceptable = this.comments.indexOf("\"Unacceptable\".</p>");
            const information = this.comments.indexOf("<p><br></p><p>The information relating");
            if (unacceptable >= 0 && information > 0) {
                const sIdx = unacceptable + 19;
                start = this.comments.substr(0, sIdx);
                ending = this.comments.substr(information);
                this.comments = this.comments.substr(sIdx, (information - sIdx));
            }

            if (!isCancelling){
                notSelected.forEach(x => {
                    const index = this.lastSelectedItemIds.indexOf(x.id);
                    if (index >= 0)
                        this.lastSelectedItemIds.splice(index, 1);
                    this.comments = this.comments.split(`, ${x.name}`).join("");
                    this.comments = this.comments.split(`${x.name}, `).join("");
                    this.comments = this.comments.split(x.name).join("");
                });
            }
        }

        if (!isCancelling) {
            const newItems = $event.detail.filter(x => x.isSelected && this.lastSelectedItemIds.indexOf(x.id) == -1);
            newItems.forEach(x => {
                this.lastSelectedItemIds.push(x.id);
            });
        } else {
            this.lastSelectedItemIds[0] = this.reasonForCancellationId;
        }

        if (!this.comments) {
            this.comments = "";
        }

        const div = document.createElement('div');
        div.innerHTML = DOMPurify.sanitize(this.comments);
        let statusesParagraph = div.querySelector('.statuses');
        if (statusesParagraph == null) {
            if(isCancelling)
            {
                const statusesTitle = document.createElement('span');
                statusesTitle.textContent = "Reason";
                div.append(statusesTitle);
            }
            statusesParagraph = document.createElement('span');
            statusesParagraph.classList.add('statuses');
            div.append(statusesParagraph);
        }
        let statusSnippet = '';

        if (isCancelling) {
            statusSnippet = this.lcCancel.itemSource.filter(x => this.lastSelectedItemIds.indexOf(x.id) != -1).map(x => x.name).join(", ");
        }

        if (this.currentRightShipInspectionStatus.id == this.unacceptable && this.mslcUnacceptable) {
            statusSnippet = this.mslcUnacceptable.itemSource.filter(x => this.lastSelectedItemIds.indexOf(x.id) != -1).map(x => x.name).join(", ");
        }

        statusesParagraph.textContent = statusSnippet;
        this.comments = div.innerHTML;
        div.remove();

        this.comments = `${start}${this.comments.replace(/<p><\/p>/g, '').replace(/<p> {2}<\/p>/g, '').replace(/<p> <\/p>/g, '')}${ending}`;

        this.selectedReasonForCancellation = $event.detail.item;
    }

    removeEmail(index) {
        this.emails.splice(index, 1);
    }

    addEmail(tech: boolean, ship: boolean, comp: boolean, defaultToCCType: boolean = false) {
        const x = {
            email: '',
            emailRecipientTypeId: defaultToCCType ? EmailRecipientTypeEnum.Cc : null,
            isInspectingCompanyEmail: comp,
            isShipManagerEmail: ship,
            isFromConfig: false,
            isTechnicalManagerEmail: tech
        };
        this.emails.push(x);
        return x;
    }

    @computedFrom("isWet")
    get subtaskTypeParameters() {
        return this.isWet ? "13" : "8";
    }
    
    @computedFrom('dateOfInspection')
    get maxDate() {
        return moment(this.dateOfInspection).add(12, 'months');
    }

    whoCancelledIdChanged(newValue)
    {
        this.selectedWhoCancelled = newValue;
        this.setCorrespondenceMessage();
    }

    async changeCloseoutType(closeoutType: any) {       
        if(!closeoutType || !closeoutType.value) return;

        this.loadingValidityMonth = true;
        this.summary = null;
        this.validUntilMonthsOffset = null;
        try {
            const computedValidity = await this.inspectionClientService.computeValidity(this.rightShipInspectionId, closeoutType.value);
            this.summary = computedValidity?.summary;
            this.validUntilMonthsOffset = computedValidity?.validityInMonths;            
        } catch (e) {
            this.notificationService.show("AI Insights", "Unable to get AI validity.", NotificationType.Warning);
            console.error(e);
        } finally {
            this.loadingValidityMonth = false;
        }
    }
}
